// Placeholder / mock data only. Shapes mirror Section 6 (unified search) and
// Section 10 (mockups) of the CanLexa spec. Replace with real API calls once
// the AI/ML backend (Part 2) is wired up — see lib/api.ts.

export type MatterType = "litigation" | "transactional";
export type Treatment = "good_law" | "distinguished" | "caution" | "overruled";

export interface Workspace {
  id: string;
  title: string;
  sub: string;
  type: MatterType;
  docs: number;
  updated: string;
}

export const WORKSPACES: Workspace[] = [
  { id: "ws-delgado", title: "Delgado v. Northline Logistics", sub: "Wrongful dismissal", type: "litigation", docs: 12, updated: "2h ago" },
  { id: "ws-harbourfront", title: "Re: Harbourfront Property Sale", sub: "Commercial real estate", type: "transactional", docs: 8, updated: "Yesterday" },
  { id: "ws-chen", title: "Chen v. 2148 Ontario Inc.", sub: "Breach of contract", type: "litigation", docs: 21, updated: "3d ago" },
  { id: "ws-meridian", title: "Meridian / Coastal Merger", sub: "M&A due diligence", type: "transactional", docs: 34, updated: "4d ago" },
  { id: "ws-salako", title: "R. v. Salako (Bail Review)", sub: "Criminal / bail", type: "litigation", docs: 6, updated: "1w ago" },
  { id: "ws-ashgrove", title: "Ashgrove Condo Dispute", sub: "Tenancy / LTB", type: "litigation", docs: 15, updated: "1w ago" },
];

export interface ExtractedFacts {
  id?: string;
  document_id?: string;
  workspace_id?: string;
  plaintiff: string;
  defendant: string;
  court: string;
  filed_date: string;
  amount_claimed: string;
  legal_issues?: string[];
  dispute_summary?: string;
  legal_issue?: string;
  status?: "pending" | "extracting" | "completed" | "error";
  error_message?: string;
}

export const MOCK_EXTRACTION: ExtractedFacts = {
  plaintiff: "Maria Delgado",
  defendant: "Northline Logistics Inc.",
  court: "Ontario Superior Court of Justice",
  legal_issue: "Wrongful dismissal — reasonable notice",
  filed_date: "March 4, 2025",
  amount_claimed: "$185,000 + costs",
  legal_issues: [
    "Wrongful dismissal — reasonable notice",
    "Mitigation of damages",
    "Calculation of benefits during notice period",
  ],
  dispute_summary:
    "Maria Delgado alleges wrongful dismissal by Northline Logistics Inc. following 8 years of continuous employment. The plaintiff contends that the severance package offered failed to provide common law reasonable notice.",
};

export interface WorkspaceDocument {
  id?: string;
  name: string;
  kind: "uploaded" | "generated";
  status?: "pending" | "extracting" | "completed" | "error";
  error_message?: string | null;
  created_at?: string;
}

export const MOCK_DOCUMENTS: WorkspaceDocument[] = [
  { name: "Employment_Agreement.pdf", kind: "uploaded" },
  { name: "Termination_Letter.pdf", kind: "uploaded" },
  { name: "Statement_of_Claim.docx", kind: "generated" },
];

export const DOCUMENT_TYPES = [
  "Smart Document Request List",
  "Research Memo",
  "Demand Letter",
];

export interface SearchResult {
  judgment_id: string;
  style_of_cause: string;
  neutral_cite: string;
  court: string;
  decided_on: string;
  score: number; // 0-1
  treatment: Treatment;
  snippet: string;
  para: number;
}

// Mirrors the /search response shape from Section 6 of the spec.
export const MOCK_SEARCH_RESULTS: SearchResult[] = [
  {
    judgment_id: "j_88",
    style_of_cause: "Woodgreen Plaza Ltd. v. Tam",
    neutral_cite: "2019 ONSC 4471",
    court: "ONSC",
    decided_on: "2019-07-11",
    score: 0.97,
    treatment: "good_law",
    snippet: "Commercial tenant repeatedly late on rent; landlord's right to terminate upheld despite partial payments.",
    para: 42,
  },
  {
    judgment_id: "j_104",
    style_of_cause: "Kingsway Holdings v. Patel Foods",
    neutral_cite: "2021 ONCA 210",
    court: "ONCA",
    decided_on: "2021-03-02",
    score: 0.94,
    treatment: "good_law",
    snippet: "Relief from forfeiture granted where arrears were cured promptly; court weighed conduct and disproportionate loss.",
    para: 17,
  },
  {
    judgment_id: "j_61",
    style_of_cause: "Riverside Mall Inc. v. 1899 Ont. Ltd.",
    neutral_cite: "2016 ONSC 1120",
    court: "ONSC",
    decided_on: "2016-02-19",
    score: 0.89,
    treatment: "distinguished",
    snippet: "Chronic late payment constituted repudiation; distinguished on facts by two later appellate decisions.",
    para: 55,
  },
  {
    judgment_id: "j_29",
    style_of_cause: "Delta Retail v. Northgate Leasing",
    neutral_cite: "2013 ONSC 8890",
    court: "ONSC",
    decided_on: "2013-11-08",
    score: 0.85,
    treatment: "caution",
    snippet: "Eviction ordered for persistent default; note: portions overtaken by 2021 ONCA 210 on relief from forfeiture.",
    para: 9,
  },
];

export const SUPPORTING_AUTHORITY = {
  style_of_cause: "Bardal v. Globe & Mail",
  neutral_cite: "[1960] O.W.N. 253",
  treatment: "good_law" as Treatment,
  cited_by_count: 4120,
};
